import React, { FC, ReactElement } from 'react';
import { ListingPageUrlState } from '../../../redux/products/types';
import { SeoData } from '../../../redux/seo/types';
import { Config } from '../../../../common/types/config';
import {
    buildPaginationHeadlinkObject,
    computeSeoIndexableAndFollow,
    hrefLang,
} from '../../../hooks/ListingPage/seo/seoUtils';
import { SEO_CONSTANTS } from '../../../hooks/ListingPage/seo/constant';
import GenericSeoHead, { LinkProps } from '../../Generic/SeoHead';
import SeoBreadcrumbScript from '../../Generic/SeoHead/SeoBreadcrumbScript';
import { convertListingPageStateToUrl } from '../../../hooks/ListingPage/listingPageUrlService';
import { Dictionary } from '../../../../../types/globals';
import { CategoryEntity } from '../../../redux/category/types';

interface Props {
    pageTitle: string;
    urlState: ListingPageUrlState;
    seoData: SeoData;
    config: Config;
    totalCount: number;
    entities: Dictionary<CategoryEntity>;
    urlKeys: Dictionary<string>;
    openGraphImage?: string;
}

export const getSeoHeadProps = (
    pageTitle: string,
    urlState: ListingPageUrlState,
    seoData: SeoData,
    totalCount: number,
    config: Config
    // eslint-disable-next-line max-params
) => {
    const {
        links: { url: host },
        listingPageSize,
    } = config;
    const robotsIndexAbleContent = seoData.metaRobots || computeSeoIndexableAndFollow(urlState);
    const shouldIndex = robotsIndexAbleContent === SEO_CONSTANTS.indexable;
    let linkForHelmet: LinkProps[] = [];
    if (shouldIndex) {
        const seoUrlState = { totalCount, urlState, listingPageSize };
        const hrefLangs = hrefLang(seoData, config, urlState);
        const prevLink = buildPaginationHeadlinkObject(SEO_CONSTANTS.prevRelKeyLink, seoUrlState, host);
        const nextLink = buildPaginationHeadlinkObject(SEO_CONSTANTS.nextRelKeyLink, seoUrlState, host);
        linkForHelmet = [...hrefLangs, prevLink, nextLink];
    }
    const seoPageTitle = pageTitle || seoData.seoTitle || SEO_CONSTANTS.siteName;
    const seoPageDescription = seoData ? seoData.seoDescription : SEO_CONSTANTS.siteName;

    return {
        robotsIndexAbleContent,
        links: linkForHelmet,
        pageTitle: seoPageTitle,
        pageDescription: seoPageDescription,
    };
};

const ListingPageSeoHead: FC<Props> = ({
    pageTitle,
    urlState,
    seoData,
    config,
    totalCount,
    entities,
    urlKeys,
    openGraphImage,
}): ReactElement => {
    const seoHeadProps = getSeoHeadProps(pageTitle, urlState, seoData, totalCount, config);
    const pageUrl = convertListingPageStateToUrl(urlState, config.links.url);

    return (
        <React.Fragment>
            <GenericSeoHead
                {...seoHeadProps}
                pageUrl={pageUrl}
                config={config}
                type={SEO_CONSTANTS.ogTypeContentValue}
                openGraphImage={openGraphImage}
            />
            <SeoBreadcrumbScript
                host={config.links.url}
                pageTitle={seoHeadProps.pageTitle}
                entities={entities}
                urlKeys={urlKeys}
                urlState={urlState}
            />
        </React.Fragment>
    );
};

export default ListingPageSeoHead;
